import React, { useState, useEffect, useRef } from 'react';
import { useAppSelector } from 'store/hooks';
import { RootState } from 'store/index';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
} from '@mui/material';
import { X, Upload, Trash2 } from 'lucide-react';
import { SiteSocial } from 'types/siteSocial';
import { Site } from 'types/site';
import { Alert } from 'components/alert/alert';

interface SiteSocialModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any, imageFile?: File) => void;
  initialData?: SiteSocial | null;
  sites: Site[];
  isLoading?: boolean;
  error?: string | null;
}

const SiteSocialModal: React.FC<SiteSocialModalProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  sites,
  isLoading,
  error,
}) => {
  const theme = useAppSelector((state: RootState) => state.ui.theme);
  const isDark = theme === 'dark';

  const [siteId, setSiteId] = useState<string | number>('');
  const [name, setName] = useState('');
  const [socialLink, setSocialLink] = useState('');
  const [sortOrder, setSortOrder] = useState<number>(0);
  const [imageUrl, setImageUrl] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (initialData) {
      setSiteId(initialData.siteId);
      setName(initialData.name || '');
      setSocialLink(initialData.socialLink);
      setSortOrder(initialData.sortOrder || 0);
      setImageUrl(initialData.imageUrl || '');
      setImagePreview(initialData.imageUrl || null);
      setImageFile(null);
    } else {
      setSiteId('');
      setName('');
      setSocialLink('');
      setSortOrder(0);
      setImageUrl('');
      setImageFile(null);
      setImagePreview(null);
    }
  }, [initialData, open]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
        setImageUrl(''); // Clear URL if file is selected
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setImageUrl('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = () => {
    const data: any = {
      siteId,
      name: name || undefined,
      socialLink,
      sortOrder,
      imageUrl: imageUrl || undefined,
    };
    onSubmit(data, imageFile || undefined);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          bgcolor: 'var(--bg-panel)',
          backgroundImage: 'none',
          color: 'var(--text-primary)',
          borderRadius: '12px',
        },
      }}
    >
      <DialogTitle
        sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {initialData ? 'Edit Social Link' : 'Add Social Link'}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: 'var(--text-secondary)' }}>
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 3,
          '&.MuiDialogContent-root': { pt: '24px !important' },
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
        }}
      >
        {error && <Alert message={error} type="error" />}

        <FormControl fullWidth size="small">
          <InputLabel sx={{ color: isDark ? 'var(--text-secondary)' : 'inherit' }}>Site</InputLabel>
          <Select
            value={siteId}
            label="Site"
            onChange={(e) => setSiteId(e.target.value)}
            sx={{
              color: isDark ? '#fff !important' : 'inherit',
              WebkitTextFillColor: isDark ? '#fff !important' : 'inherit',
              '& .MuiSelect-select': {
                color: isDark ? '#fff !important' : 'inherit',
                WebkitTextFillColor: isDark ? '#fff !important' : 'inherit',
              },
              '& .MuiSelect-icon': {
                color: isDark ? 'var(--text-secondary)' : 'inherit',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
              },
              '&.Mui-disabled': {
                color: isDark ? '#fff !important' : 'inherit',
                WebkitTextFillColor: isDark ? '#fff !important' : 'inherit',
              },
              '&.Mui-disabled .MuiSelect-select': {
                color: isDark ? '#fff !important' : 'inherit',
                WebkitTextFillColor: isDark ? '#fff !important' : 'inherit',
              },
            }}
          >
            {sites.map((site) => (
              <MenuItem key={site.id} value={site.id}>
                {site.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Name (Optional)"
            fullWidth
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Telegram"
            InputLabelProps={{ sx: { color: isDark ? 'var(--text-secondary)' : 'inherit' } }}
            inputProps={{ sx: { color: isDark ? 'var(--text-primary)' : 'inherit' } }}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
              },
            }}
          />
          {/* <TextField
            label="Sort Order"
            type="number"
            sx={{ width: '150px' }}
            size="small"
            value={sortOrder}
            onChange={(e) => setSortOrder(parseInt(e.target.value, 10) || 0)}
          /> */}
        </Box>

        <TextField
          label="Social Link"
          fullWidth
          size="small"
          required
          value={socialLink}
          onChange={(e) => setSocialLink(e.target.value)}
          placeholder="https://t.me/yourchannel"
          InputLabelProps={{ sx: { color: isDark ? 'var(--text-secondary)' : 'inherit' } }}
          inputProps={{ sx: { color: isDark ? 'var(--text-primary)' : 'inherit' } }}
          sx={{
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
            },
          }}
        />

        <Box>
          {/* <Typography
            variant="body2"
            sx={{ color: 'var(--text-secondary)', mb: 1, fontWeight: 600 }}
          >
            Icon Source (Upload file OR provide URL)
          </Typography> */}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* <TextField
              label="Image URL"
              fullWidth
              size="small"
              value={imageUrl}
              onChange={(e) => handleUrlChange(e.target.value)}
              placeholder="https://example.com/icon.png"
            /> */}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                border: '1px dashed var(--border-color)',
                borderRadius: '8px',
                bgcolor: 'rgba(255,255,255,0.02)',
              }}
            >
              {imagePreview ? (
                <Box sx={{ position: 'relative' }}>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 4,
                      objectFit: 'contain',
                      background: 'rgba(0,0,0,0.2)',
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = ''; // Clear if invalid URL
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={handleRemoveImage}
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      bgcolor: 'var(--required-red)',
                      color: 'white',
                      width: 18,
                      height: 18,
                      '&:hover': { bgcolor: 'darkred' },
                    }}
                  >
                    <Trash2 size={10} />
                  </IconButton>
                </Box>
              ) : (
                <Box
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    width: 44,
                    height: 44,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(255,255,255,0.05)',
                    borderRadius: 1,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                  }}
                >
                  <Upload size={18} color="var(--text-secondary)" />
                </Box>
              )}
              <Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    textTransform: 'none',
                    borderColor: 'var(--border-color)',
                    color: 'var(--text-primary)',
                    height: 32,
                  }}
                >
                  Upload File
                </Button>
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ color: 'var(--text-muted)', mt: 0.5 }}
                >
                  Recommended: SVG, PNG
                </Typography>
              </Box>
              <input
                type="file"
                ref={fileInputRef}
                hidden
                accept="image/*"
                onChange={handleFileChange}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, px: 3, borderTop: '1px solid var(--border-color)' }}>
        <Button onClick={onClose} sx={{ color: 'var(--text-secondary)', textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!siteId || !socialLink || (!imageFile && !imageUrl) || isLoading}
          sx={{
            bgcolor: 'var(--accent-blue)',
            color: 'white',
            textTransform: 'none',
            px: 3,
            '&:hover': { bgcolor: 'var(--accent-blue-hover)' },
          }}
        >
          {isLoading ? 'Saving...' : initialData ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SiteSocialModal;
